/**
 * census-2022-api.ts — API client para recorte censal 2022 por proyecto
 *
 * @remarks
 * Cubre los endpoints de /projects/:projectId/census-2022/*
 * Sigue el patrón axios + interceptor de auth-storage del proyecto.
 */

import axios, { type AxiosInstance } from 'axios';
import type * as GeoJSON from 'geojson';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';

// ─── Tipos de respuesta ───────────────────────────────────────────────────────

export type Census2022Status =
  | 'none'
  | 'processing_2022'
  | 'ready_2022'
  | 'stale_2022'
  | 'error_2022';

export interface CensusStatus2022 {
  census2022Status: Census2022Status;
  census2022Meta: Record<string, unknown> | null;
  slicesCount: number;
  fullRadiosCount: number;
  partialRadiosCount: number;
  radiosWithoutIndicatorsCount: number;
  projectAreaHash: string | null;
}

export interface CensusSummary2022 {
  projectId: string;
  totalRadios: number;
  fullRadios: number;
  partialRadios: number;
  radiosWithoutIndicatorsCount: number;
  projectAreaM2: number | null;
  sumIntersectionAreaM2: number | null;
  coverageAgainstProjectArea: number | null;
  areaWithoutIndicatorsM2: number | null;
  method: string | null;
  areaSource: string | null;
  projectAreaHash: string | null;
  updatedAt: string | null;
  populationTotal: number | null;
  populationTotalHh: number | null;
  pop18Plus: number | null;
  householdsTotal: number | null;
  householdsWithNbi: number | null;
  eduUniversityComplete: number | null;
  eduSecondaryCompletePlus: number | null;
  healthPublicOnly: number | null;
  healthSocialWorkPrepaid: number | null;
  householdsNoSewer: number | null;
  householdsOvercrowding: number | null;
  householdsOvercrowdingCritical: number | null;
  pctNbi: number | null;
  pctUniversity: number | null;
  pctSecondaryPlus: number | null;
  pctHealthPublicOnly: number | null;
  pctHealthSocialWork: number | null;
  pctNoSewer: number | null;
  pctOvercrowdingCritical: number | null;
}

export interface PrepareResult2022 {
  projectId: string;
  dryRun: boolean;
  totalRadios: number;
  fullRadios: number;
  partialRadios: number;
  radiosWithoutIndicatorsCount: number;
  radiosExceedingRatio: number;
  radiosClampedCount: number;
  projectAreaM2: number;
  sumIntersectionAreaM2: number;
  coverageAgainstProjectArea: number;
  areaWithoutIndicatorsM2: number;
  method: 'area_weighted';
  areaSource: 'custom_bounds' | 'bbox';
  projectAreaHash: string;
  generatedAt: string;
  durationMs: number;
  warnings: string[];
}

export interface SliceRow2022 {
  id: string;
  radioCode: string;
  censusYear: number;
  coverageRatio: number;
  coverageRatioRaw: number;
  ratioWasClamped: boolean;
  radioAreaM2: number | null;
  intersectionAreaM2: number | null;
  isFull: boolean;
  hasIndicators: boolean;
  method: string;
  areaSource: string;
  updatedAt: string;
  provinceCode: string | null;
  provinceName: string | null;
  departmentCode: string | null;
  departmentName: string | null;
  popTotalWeighted: number | null;
  householdsTotalWeighted: number | null;
  householdsWithNbiWeighted: number | null;
}

export interface SlicesResponse2022 {
  projectId: string;
  count: number;
  slices: SliceRow2022[];
}

export interface ClearSlicesResult2022 {
  projectId: string;
  deleted: number;
}

export interface CensusRadioProperties {
  radioCode: string;
  provinceName: string | null;
  departmentName: string | null;
  coverageRatio: number;
  isFull: boolean;
  hasIndicators: boolean;
  populationTotalWeighted: number | null;
  householdsTotalWeighted: number | null;
  householdsWithNbiWeighted: number | null;
  radioAreaM2: number | null;
  intersectionAreaM2: number | null;
}

export type CensusRadiosGeoJSON = GeoJSON.FeatureCollection<GeoJSON.Geometry, CensusRadioProperties>;

// ─── Axios instance ───────────────────────────────────────────────────────────

function createApiInstance(): AxiosInstance {
  const instance = axios.create({
    baseURL: API_URL,
    headers: { 'Content-Type': 'application/json' },
  });

  instance.interceptors.request.use(
    (config) => {
      const authStore = localStorage.getItem('auth-storage');
      if (authStore) {
        try {
          const parsed = JSON.parse(authStore) as { state?: { token?: string } };
          const token = parsed.state?.token;
          if (token) config.headers.Authorization = `Bearer ${token}`;
        } catch {
          // silenciar errores de parse
        }
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  return instance;
}

const api = createApiInstance();

// ─── API client ───────────────────────────────────────────────────────────────

export const census2022Api = {
  async getStatus(projectId: string): Promise<CensusStatus2022> {
    const res = await api.get<CensusStatus2022>(
      `/projects/${projectId}/census-2022/status`,
    );
    return res.data;
  },

  async getSummary(projectId: string): Promise<CensusSummary2022> {
    const res = await api.get<CensusSummary2022>(
      `/projects/${projectId}/census-2022/summary`,
    );
    return res.data;
  },

  async getSlices(
    projectId: string,
    options: { limit?: number; offset?: number } = {},
  ): Promise<SlicesResponse2022> {
    const { limit = 200, offset = 0 } = options;
    const res = await api.get<SlicesResponse2022>(
      `/projects/${projectId}/census-2022/slices`,
      { params: { limit, offset } },
    );
    return res.data;
  },

  async prepare(
    projectId: string,
    options: { dryRun?: boolean; force?: boolean } = {},
  ): Promise<PrepareResult2022> {
    const params: Record<string, string> = {};
    if (options.dryRun) params['dryRun'] = '1';
    if (options.force)  params['force']  = '1';
    const res = await api.post<PrepareResult2022>(
      `/projects/${projectId}/census-2022/prepare`,
      undefined,
      { params },
    );
    return res.data;
  },

  async recalculate(
    projectId: string,
    options: { dryRun?: boolean } = {},
  ): Promise<PrepareResult2022> {
    const params: Record<string, string> = {};
    if (options.dryRun) params['dryRun'] = '1';
    const res = await api.post<PrepareResult2022>(
      `/projects/${projectId}/census-2022/recalculate`,
      undefined,
      { params },
    );
    return res.data;
  },

  async clearSlices(projectId: string): Promise<ClearSlicesResult2022> {
    const res = await api.delete<ClearSlicesResult2022>(
      `/projects/${projectId}/census-2022/slices`,
    );
    return res.data;
  },

  async getRadiosGeoJSON(projectId: string): Promise<CensusRadiosGeoJSON> {
    const res = await api.get<CensusRadiosGeoJSON>(
      `/projects/${projectId}/census-2022/radios/geojson`,
    );
    return res.data;
  },
};
